import { Injectable } from '@angular/core';
import {
  Observable,
  Subject,
  Subscription,
  timer,
} from 'rxjs';
import { Platform } from '@ionic/angular';

@Injectable()
export class FrontendDropdownService {
  viewportResizeFinish$: Observable<number>;
  keyboardHeight = 0;

  keyWaitTime = 500;
  currentKeys: string[] = [];

  private timerSubs: Subscription[] = [];
  private readonly subscription = new Subscription();
  private readonly viewportResizeFinishSource$ = new Subject<number>();

  constructor(private readonly platform: Platform) {
    this.viewportResizeFinish$ = this.viewportResizeFinishSource$.asObservable();
    visualViewport?.addEventListener('resize', this.trackResize);

    const showSub = this.platform.keyboardDidShow
      .subscribe((ev) => {
        const { keyboardHeight } = ev;
        this.keyboardHeight = keyboardHeight;
      });
    this.subscription.add(showSub);

    const hideSub = this.platform.keyboardDidHide
      .subscribe(() => {
        this.keyboardHeight = 0;
      });
    this.subscription.add(hideSub);
  }

  trackResize = (ev: Event): void => {
    this.viewportResizeFinishSource$.next((ev.target as VisualViewport).height);
  };

  noBubble(interaction: MouseEvent | TouchEvent): void {
    interaction.stopPropagation();
  }

  keySearchByKey<T extends Record<string, unknown>>(
    keyboard: KeyboardEvent,
    displayItems: T[],
    bindLabel: string,
    bindValue: string,
    listWrapper: HTMLDivElement,
  ): void {
    if (!listWrapper) {
      return;
    }

    this.waitTimer(keyboard.key, (str) => {
      this.keySearch(
        str,
        displayItems,
        listWrapper,
        item => String((item as T)[bindLabel]),
        item => String((item as T)[bindValue]),
      );
    });
  }

  keySearchInArray(
    keyboard: KeyboardEvent,
    displayItems: string[],
    listWrapper: HTMLDivElement,
  ): void {
    if (!listWrapper) {
      return;
    }

    this.waitTimer(keyboard.key, (str) => {
      this.keySearch(
        str,
        displayItems,
        listWrapper,
        item => String(item),
        item => String(item),
      );
    });
  }

  unsub(): void {
    visualViewport?.removeEventListener('resize', this.trackResize);
    this.subscription.unsubscribe();
    this.timerSubs.forEach((sub) => {
      sub.unsubscribe();
    });
    this.timerSubs = [];
  }

  private waitTimer(keyCode: string, callback: (str: string) => void): void {
    this.unsub();
    this.currentKeys.push(keyCode);

    const timerSub = timer(this.keyWaitTime)
      .subscribe(() => {
        callback(this.currentKeys.join(''));
        this.currentKeys = [];
      });
    const newSub = new Subscription();
    newSub.add(timerSub);
    this.timerSubs.push(newSub);
  }

  private keySearch(
    searchString: string,
    displayItems: unknown[],
    listWrapper: HTMLDivElement,
    searchItem: (item: unknown) => string,
    searchElement: (item: unknown) => string,
  ): void {
    const foundItem = displayItems.find(item => searchItem((item))
      .toLowerCase()
      .startsWith(searchString.toLowerCase()));
    if (!foundItem) {
      return;
    }

    const targetItem = Object.values(listWrapper.children)
      .find(value => value.id === `display-item-${searchElement(foundItem)}`);
    if (!targetItem) {
      return;
    }

    targetItem.scrollIntoView();
  }
}
